import { useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Info, LogOut, Crown, Settings, Headset, Sparkles, Moon, Sun, Palette, Globe, Monitor, ChevronDown, TriangleAlert, BarChart3, UserCog } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  // DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
} from 'components/ui/dropdown-menu';
import { Sheet, SheetContent } from 'components/ui/sheet';
import { useSignoutMutation } from 'features/auth/hooks/use-auth';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useNavigate, useLocation } from 'react-router-dom';
import DummyProfile from 'assets/images/dummy_profile.png';
import { UProfileItem } from '/pages/main/uprofile/types/uprofile-type';
import { ArchiveChat } from 'pages/archive/archive-chat';
import { useTranslation } from 'react-i18next';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useQueryClient } from "@tanstack/react-query";
import { completeLogout } from 'features/auth/utils/complete-logout';
import { useSubscriptionStore } from 'state/store/subscription/useSubscriptionStore';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { SettingsDialog } from '../settings-dialog';
import seliseLogo from 'assets/images/selise-ai-logo.svg';
import seliseLogoDark from 'assets/images/selise-ai-logo-dark.svg';
import { KnowledgeBaseModal } from 'features/profile/components/knowledge-base-modal';
import { PLANS } from 'constant/plan';
import ContactModal from 'features/subscription/components/contact-sales-modal';
import { useContactSales } from 'features/subscription/hooks/use-subscription';
import { useConfig } from 'hooks/use-config';
import { useLanguageContext } from 'i18n/language-context';
import { useSecureConfigStore } from 'state/store/config/useConfigStore';
import { useTheme } from 'components/core/theme-provider';
import { updateCurrentLanguage } from '../language-selector/services/language-service';
import { useIsMobile } from 'hooks/use-mobile';
import { cn } from 'lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { isIncompleteCheckout } from 'features/subscription/utils/resolve-plan-select-action';
import { sidebarFooterPillClass, sidebarFooterPillIconColorClass, sidebarFooterPillLabelClass, SIDEBAR_FOOTER_RIGHT_ICON_SIZE } from 'components/blocks/layout/sidebar-footer-pill';
import { useGetOrganizationQuery } from 'features/organization';
import { getRoleTranslationKey, ROLES } from 'constant/roles';

/**
 * UProfileMenu Component
 *
 * A user profile dropdown menu component that displays user information and provides
 * navigation and account management options.
 *
 * Features:
 * - Displays user profile image and name
 * - Shows loading states with skeleton placeholders
 * - Provides navigation to profile page
 * - Includes theme toggling functionality
 * - Handles user logout with authentication state management
 * - Responsive design with different spacing for mobile and desktop
 *
 * Dependencies:
 * - Requires useTheme hook for theme management
 * - Requires useAuthStore for authentication state management
 * - Requires useSignoutMutation for API logout functionality
 * - Requires useGetAccount for fetching user account data
 * - Uses DropdownMenu components for the menu interface
 * - Uses React Router's useNavigate for navigation
 *
 * @returns {JSX.Element} A dropdown menu component for user profile management
 *
 * @example
 * // Basic usage in a header or navigation component
 * <UProfileMenu />
 */

const profileMenuTextClass = 'text-sm leading-[22px] font-normal text-[#3D3D3D] dark:text-[#ffffff]';
const profileMenuSubItemClass = '!text-[13px] !leading-[22px] font-normal';
const profileDropdownItemClass =
  'w-full rounded-none px-4 gap-4 min-h-[34px] py-[6px]';

export const UProfileMenu = ({
  isDropdownIconRequired,
  isFullNameRequired,
  variant = 'avatar',
}: UProfileItem) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isLanguageExpanded, setIsLanguageExpanded] = useState(false);
  const [isAppearanceExpanded, setIsAppearanceExpanded] = useState(false);
  const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isKnowledgeBaseModalOpen, setIsKnowledgeBaseModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { theme, selectedTheme, setTheme } = useTheme();

  // Contact sales integration
  const { mutateAsync: submitContactForm } = useContactSales();
  const { configurationSettings } = useConfig();
  const { currentLanguage, setLanguage, availableLanguages } = useLanguageContext();
  const { siteConfigurationSettings } = useSecureConfigStore();

  // Check if subscription info should be shown (only show if explicitly true)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const showSubscriptionInfo = siteConfigurationSettings?.siteConfigurations?.data?.showSubscriptionInfo === true;

  const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
  const { mutateAsync } = useSignoutMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const user = useCommonStore((state) => state.user);
  const currentPlan = useSubscriptionStore((state) => state.currentPlan);
  const subscriptionDetails = useSubscriptionStore((state) => state.subscriptionDetails);
  const planName = subscriptionDetails?.planName;

  const activeOrganizationId = selectedOrgId || user?.organizationId || '';
  const { data: organizationData } = useGetOrganizationQuery(activeOrganizationId);

  const signoutHandler = async () => {
    try {
      const res = await mutateAsync();
      if (res.isSuccess) {
        completeLogout(queryClient, navigate);
      }
    } catch (_error) {
      /* empty */
    }
  };

  const handleContactSubmit = async (data: any) => {
    const payload = {
      language: currentLanguage,
      message: data?.message,
      organizationName: data?.company,
      contactName: data?.name,
      phone: data?.phone,
      emailTo: configurationSettings.paths.salesContactMailTo,
      email: data?.email,
    };

    return await submitContactForm(payload);
  };

  const changeLanguage = async (newLanguageCode: string) => {
    if (user) {
      updateCurrentLanguage(user.id, newLanguageCode, configurationSettings);
    }
    await setLanguage(newLanguageCode);
  };

  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'User';
  const planBadgeLabel = planName
    ? (planName.toLowerCase().includes('plan') ? planName : `${planName}`)
    : 'No Plan';
  const isPendingCheckout = isIncompleteCheckout(subscriptionDetails?.status);
  const planBadgeClassName = cn(
    'shrink-0 inline-flex items-center gap-1 rounded-full border px-3 py-0.5 text-[10px] font-semibold',
    isPendingCheckout
      ? 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300'
      : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-300'
  );
  const planBadge = planBadgeLabel ? (
    isPendingCheckout ? (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={planBadgeClassName}>
              <TriangleAlert className="h-2.5 w-2.5 shrink-0" />
              {planBadgeLabel}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {t('PAYMENT_PENDING', 'Payment pending')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      <span className={planBadgeClassName}>
        <Sparkles className="h-2.5 w-2.5 shrink-0" />
        {planBadgeLabel}
      </span>
    )
  ) : null;
  const highestRole = [ROLES.ADMIN, ROLES.USER]
    .find((roleName) => user?.roles?.includes(roleName));
  const isAdmin = Boolean(user?.roles?.includes(ROLES.ADMIN));
  const showUpgrade = isAdmin;
  const role = highestRole
    ? t(getRoleTranslationKey(highestRole))
    : t('NO_ROLE') || 'No role';
  const themeLabel =
    selectedTheme === 'system' ? t('SYSTEM') || 'System' : selectedTheme === 'dark' ? t('DARK') || 'Dark' : t('LIGHT') || 'Light';
  const ThemeIcon = selectedTheme === 'system' ? Monitor : theme === 'dark' ? Moon : Sun;

  const resetExpandedSections = () => {
    setIsLanguageExpanded(false);
    setIsAppearanceExpanded(false);
  };

  const handleDropdownOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
    if (!open) {
      resetExpandedSections();
    }
  };

  const handleSheetOpenChange = (open: boolean) => {
    setIsSheetOpen(open);
    if (!open) {
      resetExpandedSections();
    }
  };

  const handleOpenSettings = () => {
    setIsSettingsDialogOpen(true);
    if (isMobile) {
      setIsSheetOpen(false);
    }
  };

  const handleOpenUsage = () => {
    navigate('/usage');
    setIsDropdownOpen(false);
    setIsSheetOpen(false);
  };

  const handleOpenAdmin = () => {
    navigate('/identity-management');
    setIsDropdownOpen(false);
    setIsSheetOpen(false);
  };

  const handleOpenUpgrade = () => {
    navigate('/subscription-plans');
    setIsDropdownOpen(false);
    setIsSheetOpen(false);
  };
  const handleOpenAbout = () => {
    setIsAboutDialogOpen(true);
    setIsDropdownOpen(false);
    setIsSheetOpen(false);
  };

  const handleSignOut = () => {
    if (isMobile) {
      setIsSheetOpen(false);
    }
    signoutHandler();
  };

  const profileAvatar = (
    <div className="cursor-pointer rounded-full hover:bg-accent transition-colors">
      <div className="relative w-7 h-7">
        <img
          src={user?.profileImageUrl || DummyProfile}
          alt="profile"
          loading="lazy"
          className="object-cover w-7 h-7 rounded-full"
        />

        {currentPlan && currentPlan.planCode && currentPlan.planCode !== PLANS.NOPLAN && currentPlan.planCode !== PLANS.FREE && (
          <div className="absolute -bottom-0.5 -right-0.5 bg-true-white border border-border dark:border-transparent rounded-full p-0.5">
            <Crown className="h-2 w-2 text-primary fill-primary"/>
          </div>
        )}
      </div>
    </div>
  );

  const profilePillButton = (onClick?: React.MouseEventHandler<HTMLButtonElement>) => (
    <button
      type="button"
      className={sidebarFooterPillClass}
      onClick={onClick}
      aria-label={t('PROFILE') || 'Profile'}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <img
          src={user?.profileImageUrl || DummyProfile}
          alt="profile"
          loading="lazy"
          className="h-6 w-6 shrink-0 rounded-full object-cover"
        />
        {isFullNameRequired && (
          <div className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
            <span className={cn(sidebarFooterPillLabelClass, 'truncate')}>{fullName}</span>
            {planBadge}
          </div>
        )}
      </div>
      {isDropdownIconRequired && (
        <ChevronDown
          className={cn('shrink-0 rotate-180', sidebarFooterPillIconColorClass)}
          size={SIDEBAR_FOOTER_RIGHT_ICON_SIZE}
          strokeWidth={1.75}
        />
      )}
    </button>
  );

  const profilePill = profilePillButton();

  const profileTrigger = variant === 'pill' ? profilePill : profileAvatar;

  const profileHeader = (
    <div className="px-4 pb-4 border-b border-border">
      <div className="text-xs leading-[18px] font-normal word-break break-words truncate">
        {user?.email}
      </div>
      <div className="text-[13px] leading-[22px] font-normal">
        {role}
      </div>
      {organizationData?.name && organizationData.name !== 'Default' && (
        <div className="text-[13px] leading-[22px] font-normal text-muted-foreground truncate">
          {organizationData.name}
        </div>
      )}
    </div>
  );

  const mobileLanguageSection = (
    <div className="flex flex-col">
      <button
        type="button"
        className="bg-background hover:bg-accent flex w-full items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
        onClick={() => setIsLanguageExpanded((prev) => !prev)}
      >
        <Globe className="h-4 w-4 text-foreground shrink-0" />
        <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>
          {t('LANGUAGE') || 'Language'}:{' '}
          <span
            className={`${theme === 'dark' ? 'dark:border-black-500' : 'text-primary-600'} font-medium ml-1`}
          >
            {currentLanguage.split('-')[0].toUpperCase()}
          </span>
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-foreground shrink-0 transition-transform',
            isLanguageExpanded && 'rotate-180'
          )}
        />
      </button>
      {isLanguageExpanded ? (
        <div className="ml-8 mt-1 flex flex-col gap-0.5">
          {availableLanguages?.map((lang) => (
            <button
              key={lang.itemId}
              type="button"
              className={cn(
                profileMenuTextClass,
                'px-3 py-[6px] cursor-pointer rounded-[6px] text-left hover:bg-accent transition-colors',
                lang.languageCode === currentLanguage
                  ? 'text-primary-600'
                  : 'text-typography-grey-1-secondary'
              )}
              onClick={() => changeLanguage(lang.languageCode)}
            >
              {lang.languageName}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );

  const mobileAppearanceSection = (
    <div className="flex flex-col">
      <button
        type="button"
        className="bg-background hover:bg-accent flex w-full items-center px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
        onClick={() => setIsAppearanceExpanded((prev) => !prev)}
      >
        <Palette className="h-4 w-4 text-foreground mr-4 shrink-0" />
        <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>
          {t('APPEARANCE') || 'Appearance'}:{' '}
          <span
            className={`${theme === 'dark' ? 'dark:border-black-500' : 'text-primary-600'} font-medium mr-1 capitalize truncate`}
          >
            {themeLabel}
          </span>
        </span>
        <ThemeIcon
          className={`h-4 w-4 shrink-0 ${theme === 'dark' ? 'dark:border-black-500' : 'text-primary-600'}`}
        />
        <ChevronDown
          className={cn(
            'h-4 w-4 text-foreground shrink-0 ml-2 transition-transform',
            isAppearanceExpanded && 'rotate-180'
          )}
        />
      </button>
      {isAppearanceExpanded ? (
        <div className="ml-8 mt-1 flex flex-col gap-0.5">
          <button
            type="button"
            className={cn(
              profileMenuTextClass,
              'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] hover:bg-accent transition-colors',
              selectedTheme === 'system' ? 'text-primary-600' : 'text-typography-grey-1-secondary'
            )}
            onClick={() => setTheme('system')}
          >
            <span>{t('SYSTEM') || 'System'}</span>
            <Monitor className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={cn(
              profileMenuTextClass,
              'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] hover:bg-accent transition-colors',
              selectedTheme === 'dark' ? 'text-primary-600' : 'text-typography-grey-1-secondary'
            )}
            onClick={() => setTheme('dark')}
          >
            <span>{t('DARK') || 'Dark'}</span>
            <Moon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={cn(
              profileMenuTextClass,
              'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] hover:bg-accent transition-colors',
              selectedTheme === 'light' ? 'text-primary-600' : 'text-typography-grey-1-secondary'
            )}
            onClick={() => setTheme('light')}
          >
            <span>{t('LIGHT') || 'Light'}</span>
            <Sun className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </div>
  );

  const mobileMenuContent = (
    <div className="flex flex-col h-full pt-3 pb-[14px]">
      {profileHeader}

      <div className="flex flex-col mt-[14px] mx-2 pb-[14px] border-b-[0.5px] border-border">
        <button
          type="button"
          className="bg-background hover:bg-accent flex items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
          onClick={handleOpenSettings}
        >
          <Settings className="h-4 w-4 text-foreground" />
          <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>{t('SETTINGS') || 'Settings'}</span>
        </button>
        <button
          type="button"
          className="bg-background hover:bg-accent flex items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
          onClick={handleOpenUsage}
        >
          <BarChart3 className="h-4 w-4 text-foreground" />
          <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>{t('USAGE') || 'Usage'}</span>
        </button>
        {showUpgrade && (
          <button
            type="button"
            className="bg-background hover:bg-accent flex items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
            onClick={handleOpenUpgrade}
          >
            <Sparkles className="h-4 w-4 text-foreground" />
            <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>{t('UPGRADE') || 'Upgrade'}</span>
          </button>
        )}
        {isAdmin && (
          <button
            type="button"
            className="bg-background hover:bg-accent flex items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
            onClick={handleOpenAdmin}
          >
            <UserCog className="h-4 w-4 text-foreground" />
            <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>{t('ADMIN') || 'Admin'}</span>
          </button>
        )}
        {mobileLanguageSection}
        {mobileAppearanceSection}
      </div>

      <div className="mx-4 my-0 h-px bg-border/90" />

      <div className="mx-2 my-[14px]">
        <button
          type="button"
          className="bg-background hover:bg-accent flex w-full items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
          onClick={handleOpenAbout}
        >
          <Info className="h-4 w-4 text-foreground" />
          <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>{t('ABOUT') || 'About'}</span>
        </button>
      </div>

      <div className="mx-4 my-0 h-px bg-border/90" />

      <button
        type="button"
        className="bg-background hover:bg-accent flex items-center mx-2 mt-[14px] gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
        onClick={handleSignOut}
      >
        <LogOut className="h-4 w-4 text-foreground" />
        <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>{t('LOG_OUT') || 'Log out'}</span>
      </button>
    </div>
  );

  const dialogs = (
    <>
      {isModalOpen && <ArchiveChat isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
      <KnowledgeBaseModal
        isOpen={isKnowledgeBaseModalOpen}
        onClose={() => setIsKnowledgeBaseModalOpen(false)}
      />
      <Dialog open={isAboutDialogOpen} onOpenChange={setIsAboutDialogOpen}>
        <DialogContent className="max-w-[360px] gap-0 border-0 bg-background p-0 shadow-2xl">
          <div className="flex flex-col items-center gap-6 px-8 py-10">
            <div className="flex flex-col items-center gap-2 text-center">
              <img
                src={theme === 'dark' ? seliseLogoDark : seliseLogo}
                alt="SELISE AI"
                className="h-10 w-auto object-contain"
              />
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                {t('VERSION') || 'Version'} {__APP_VERSION__} ({__BUILD_SHA__})
              </p>
            </div>
          </div>

          <div className="border-t border-border/50 p-6">
            <Button
              variant="ghost"
              className="w-full font-medium text-foreground hover:bg-accent/50"
              onClick={() => setIsAboutDialogOpen(false)}
            >
              {t('CLOSE') || 'Close'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <SettingsDialog
        isOpen={isSettingsDialogOpen}
        setIsOpen={setIsSettingsDialogOpen}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSubmit={handleContactSubmit}
        title={t('CONTACT_US')}
        initialData={{
          name: fullName,
          email: user?.email || "",
          phone: user?.phoneNumber || "",
        }}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className={variant === 'pill' ? 'w-full' : undefined}>
        {variant === 'pill' ? (
          profilePillButton(() => setIsSheetOpen(true))
        ) : (
          <button
            type="button"
            onClick={() => setIsSheetOpen(true)}
            aria-label={t('PROFILE') || 'Profile'}
          >
            {profileAvatar}
          </button>
        )}
        <Sheet open={isSheetOpen} onOpenChange={handleSheetOpenChange}>
          <SheetContent
            side="right"
            className="w-[280px] sm:max-w-[280px] p-0 border-border shadow-primary-soft"
          >
            {mobileMenuContent}
          </SheetContent>
        </Sheet>
        {dialogs}
      </div>
    );
  }

  return (
    <div className={variant === 'pill' ? 'w-full min-w-0' : undefined}>
      <DropdownMenu open={isDropdownOpen} onOpenChange={handleDropdownOpenChange}>
        <DropdownMenuTrigger asChild className={variant === 'pill' ? 'w-full' : undefined}>
          {profileTrigger}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="flex w-[270.6px] flex-col items-stretch rounded-[8px] border-[0.5px] bg-background p-0 pb-[14px] pt-3 shadow-primary-soft"
          align={variant === 'pill' ? 'start' : 'end'}
          side="top"
          sideOffset={10}
        >
          {profileHeader}

          {/* Group 1: Settings, Language, Appearance */}
          <div className="mt-[14px] flex w-full flex-col border-b border-border/90 pb-[14px]">
            <DropdownMenuItem
              className={profileDropdownItemClass}
              onClick={handleOpenSettings}
            >
              <Settings className="h-4 w-4 shrink-0 text-foreground" />
              <span className={cn(profileMenuTextClass, 'flex-1')}>{t('SETTINGS') || 'Settings'}</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              className={profileDropdownItemClass}
              onClick={handleOpenUsage}
            >
              <BarChart3 className="h-4 w-4 shrink-0 text-foreground" />
              <span className={cn(profileMenuTextClass, 'flex-1')}>{t('USAGE') || 'Usage'}</span>
            </DropdownMenuItem>

            {showUpgrade && (
              <DropdownMenuItem
                className={profileDropdownItemClass}
                onClick={handleOpenUpgrade}
              >
                <Sparkles className="h-4 w-4 shrink-0 text-foreground" />
                <span className={cn(profileMenuTextClass, 'flex-1')}>{t('UPGRADE') || 'Upgrade'}</span>
              </DropdownMenuItem>
            )}

            {isAdmin && (
              <DropdownMenuItem
                className={profileDropdownItemClass}
                onClick={handleOpenAdmin}
              >
                <UserCog className="h-4 w-4 shrink-0 text-foreground" />
                <span className={cn(profileMenuTextClass, 'flex-1')}>{t('ADMIN') || 'Admin'}</span>
              </DropdownMenuItem>
            )}

            {/* Language Sub-menu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className={profileDropdownItemClass}>
                <Globe className="h-4 w-4 shrink-0 text-foreground" />
                <span className={cn(profileMenuTextClass, 'flex-1')}>{t('LANGUAGE') || 'Language'}: <span className={`${theme === 'dark' ? 'text-[#ffff]' : 'text-black'} font-medium ml-1`}>{currentLanguage.split('-')[0].toUpperCase()}</span></span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-background border border-border p-1.5 rounded-[12px] min-w-[140px] shadow-xl" sideOffset={12}>
                {availableLanguages?.map((lang) => (
                  <DropdownMenuItem
                    key={lang.itemId}
                    className={cn(
                      profileMenuSubItemClass,
                      'px-3 py-[6px] cursor-pointer rounded-[6px] focus:bg-accent',
                      lang.languageCode === currentLanguage
                        ? 'text-primary-600'
                        : 'text-typography-grey-1-secondary'
                    )}
                    onClick={() => changeLanguage(lang.languageCode)}
                  >
                    {lang.languageName}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {/* Appearance Sub-menu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className={profileDropdownItemClass}>
                <Palette className="h-4 w-4 shrink-0 text-foreground" />
                <span className={cn(profileMenuTextClass, 'flex-1')}>
                  {t('APPEARANCE') || 'Appearance'}: <span className={`${theme === 'dark' ? 'text-[#ffffff]' : 'text-black'} font-medium ml-1 capitalize truncate`}>
                    {themeLabel}
                  </span>
                </span>
                <ThemeIcon className={`h-4 w-4 shrink-0 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-background border border-border p-1.5 rounded-[12px] min-w-[140px] shadow-xl" sideOffset={12}>
                <DropdownMenuItem
                  className={cn(
                    profileMenuSubItemClass,
                    'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] focus:bg-accent',
                    selectedTheme === 'system' ? 'text-primary-600' : 'text-typography-grey-1-secondary'
                  )}
                  onClick={() => setTheme('system')}
                >
                  <span>{t('SYSTEM') || 'System'}</span>
                  <Monitor className="h-4 w-4" />
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={cn(
                    profileMenuSubItemClass,
                    'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] focus:bg-accent',
                    selectedTheme === 'dark' ? 'text-primary-600' : 'text-typography-grey-1-secondary'
                  )}
                  onClick={() => setTheme('dark')}
                >
                  <span>{t('DARK') || 'Dark'}</span>
                  <Moon className="h-4 w-4" />
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={cn(
                    profileMenuSubItemClass,
                    'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] focus:bg-accent',
                    selectedTheme === 'light' ? 'text-primary-600' : 'text-typography-grey-1-secondary'
                  )}
                  onClick={() => setTheme('light')}
                >
                  <span>{t('LIGHT') || 'Light'}</span>
                  <Sun className="h-4 w-4" />
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </div>

          <DropdownMenuSeparator className="my-0 bg-border/90" />

          {/* Group 3: About */}
          <DropdownMenuItem
            className={cn(profileDropdownItemClass, 'my-[14px]')}
            onClick={handleOpenAbout}
          >
            <Info className="h-4 w-4 shrink-0 text-foreground" />
            <span className={cn(profileMenuTextClass, 'flex-1')}>{t('ABOUT') || 'About'}</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator className="my-0 bg-border/90" />

          {/* Group 4: Log out */}
          <DropdownMenuItem
            className={cn(profileDropdownItemClass, 'mt-[14px]')}
            onClick={handleSignOut}
          >
            <LogOut className="h-4 w-4 shrink-0 text-foreground" />
            <span className={cn(profileMenuTextClass, 'flex-1')}>{t('LOG_OUT') || 'Log out'}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogs}
    </div>
  );
};
